import type { ReactNode } from 'react';
import { Tooltip } from '@odo/shared-ui';
import { AiGlyph } from './AiGlyph';

const AI_MARKER_TOOLTIP = 'Set by AI. Change it to override.';
// violet-500, not 400: the glyph alone carries the meaning, so it needs 3:1 against white.
const MARKER_CLASS = 'inline-flex items-center justify-center text-violet-500';
const PLACEMENT_CLASSES = {
    beside: {
        wrapper: 'inline-flex items-center gap-1.5',
        marker: `${MARKER_CLASS} h-4 w-[18px] flex-shrink-0`,
        glyph: 'text-[11px]'
    },
    // Sits on the trigger's top border, clear of the chevron, so narrow columns needn't fit it.
    corner: {
        wrapper: 'relative inline-flex max-w-full',
        marker: `${MARKER_CLASS} absolute -top-1.5 right-1 h-3.5 w-3.5 rounded-full bg-white ring-1 ring-violet-200`,
        glyph: 'text-[9px]'
    }
};

type MarkerPlacement = keyof typeof PLACEMENT_CLASSES;

function AiSetMarker({ placement }: { placement: MarkerPlacement }) {
    const classes = PLACEMENT_CLASSES[placement];
    return (
        <Tooltip content={AI_MARKER_TOOLTIP}>
            <span className={classes.marker}>
                <AiGlyph className={classes.glyph} />
                <span className="sr-only">{AI_MARKER_TOOLTIP}</span>
            </span>
        </Tooltip>
    );
}

interface AiMarkedControlProps {
    byAi: boolean;
    // Keeps the marker's slot when absent, so stacked rows line their pickers up.
    reserveSpace?: boolean;
    placement?: MarkerPlacement;
    children: ReactNode;
}

// Always wraps, so the picker isn't remounted when an override drops the marker.
export function AiMarkedControl({
    byAi,
    reserveSpace = false,
    placement = 'beside',
    children
}: AiMarkedControlProps) {
    return (
        <div className={PLACEMENT_CLASSES[placement].wrapper}>
            {children}
            {byAi && <AiSetMarker placement={placement} />}
            {!byAi && reserveSpace && <span className="w-[18px]" aria-hidden="true" />}
        </div>
    );
}
